import { FontProps } from 'common/utils/styleUtilsNew'
import React from 'react'
import { EventInterface, EventLocationInterface } from '../calendar-interface'
import { EDITOR_DURATION_PLACEHOLDER } from '../calendar-step/event-info'
import {
  EventLocationTypeEnum,
  getReadableEventLocation,
  getReadableEventLocationIcon,
  TimeFormatEnum,
} from '../constants'
import { CalendarCheckIcon } from '../ui/icons/CalendarCheckIcon'
import { CameraIcon } from '../ui/icons/CameraIcon'
import { HourglassIcon } from '../ui/icons/HourglassIcon'
import { UserIcon } from '../ui/icons/UserIcon'
import { WorldIcon2 } from '../ui/icons/WorldIcon2'
import { formatDateTime } from '../utils'
import { BookingSummaryItemUi } from './ui/booking-summary-item-ui'
import { BookingSummaryUi } from './ui/booking-summary-ui'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'

export interface BookingSummaryProps {
  event?: EventInterface
  color?: string
  selectedTimeZone: string
  selectedDate?: Date
  selectedLocationId?: EventLocationInterface['id']
  selectedTimeSlot?: number
  timeFormat?: TimeFormatEnum
}

export const BookingSummary = ({
  event,
  color,
  selectedTimeZone,
  selectedDate,
  selectedLocationId,
  selectedTimeSlot,
  timeFormat,
}: BookingSummaryProps & FontProps) => {
  const { t } = useTypedTranslation()
  const details: {
    key: string
    value?: string | number
    icon: React.ElementType
  }[] = [
    {
      key: 'hostName',
      value: event?.hostName || t('components.core.calendar.empty_event_name'),
      icon: UserIcon,
    },
    {
      key: 'date',
      value:
        (selectedDate &&
          typeof selectedTimeSlot === 'number' &&
          formatDateTime(selectedDate, selectedTimeSlot, t, timeFormat)) ||
        '',
      icon: CalendarCheckIcon,
    },
    {
      key: 'duration',
      value: t(
        'entity_settings.booking_calendar.booking_summary.duration_minutes_icu',
        {
          minutes: event?.eventDuration || EDITOR_DURATION_PLACEHOLDER,
        },
      ),
      icon: HourglassIcon,
    },
    {
      key: 'timeZone',
      value: selectedTimeZone,
      icon: WorldIcon2,
    },
  ]

  return (
    <BookingSummaryUi>
      <p style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>
        {event?.name}
      </p>
      {details.map(({ key, value, icon: Icon }) => (
        <BookingSummaryItemUi key={key}>
          <Icon fill={color} />
          <span>{value}</span>
        </BookingSummaryItemUi>
      ))}
      <div>
        {!event?.locations && (
          <BookingSummaryItemUi>
            <CameraIcon fill={color} />
            <span>
              {t(getReadableEventLocation(EventLocationTypeEnum.OnlineMeeting))}
            </span>
          </BookingSummaryItemUi>
        )}
        {event?.locations.map(({ locationType, id }) => {
          if (selectedLocationId !== id) return
          const Icon = getReadableEventLocationIcon(locationType)
          return (
            <BookingSummaryItemUi key={id}>
              <Icon fill={color} />
              <span>{t(getReadableEventLocation(locationType))}</span>
            </BookingSummaryItemUi>
          )
        })}
      </div>
    </BookingSummaryUi>
  )
}
